import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from '@mui/material';

import { LoadingButton } from '../../common/LoadingButton';
import {
  useCreateLLMModel,
  useLLMInvocationMethods,
  useLLMTaskTypes,
  useUpdateLLMModel,
} from '../../../services/llm-catalog.service';
import {
  LLMInvocationMethod,
  LLMModel,
  LLMModelFormValues,
  LLMModelInvocationProfileInput,
  LLMProvider,
  LLMTaskTypeOption,
} from '../../../types/admin';
import { modelTaskTypeCodes, synthesizeLegacyProfiles, toPositiveInteger } from './helpers';
import { buildModelConfig, splitModelConfig, validateExtraConfig } from './modelConfig';
import { ModelBasicTab } from './ModelBasicTab';
import { ModelInputTab } from './ModelInputTab';
import { BUILT_IN_METHOD_DEFINITIONS, ModelInvocationTab } from './ModelInvocationTab';
import { LLMModelJsonField } from './types';
import {
  buildModelPricesPayload,
  isResolutionPrice,
  RESOLUTION_DIMENSION_KEY,
  unitPricesFromModel,
} from './modelPricing';
import { findDuplicateUnitPriceIdentities } from './unitPriceIdentity';

export interface ModelDialogProps {
  open: boolean;
  providers: LLMProvider[];
  providerPublicId?: string;
  model?: LLMModel | null;
  onClose: () => void;
}

export const ModelDialog: React.FC<ModelDialogProps> = ({
  open,
  providers,
  providerPublicId,
  model,
  onClose,
}) => {
  const createModel = useCreateLLMModel();
  const updateModel = useUpdateLLMModel();
  const isSaving = createModel.isPending || updateModel.isPending;
  const { data: taskTypes = [], isLoading: isLoadingTaskTypes } = useLLMTaskTypes();
  const { data: invocationMethodsData } = useLLMInvocationMethods();
  const [activeTab, setActiveTab] = React.useState<'basic' | 'input' | 'invocation'>('basic');
  const [jsonErrors, setJsonErrors] = React.useState<Partial<Record<LLMModelJsonField, string>>>(
    {}
  );
  const [invocationProfiles, setInvocationProfiles] = React.useState<
    LLMModelInvocationProfileInput[]
  >([]);
  const [invocationError, setInvocationError] = React.useState('');
  const [values, setValues] = React.useState<LLMModelFormValues>({
    providerPublicId: providerPublicId ?? '',
    taskTypeCode: '',
    taskTypeCodes: [],
    code: '',
    name: '',
    config: '',
    usageScope: 'both',
    inputPricePerMillionTokens: null,
    outputPricePerMillionTokens: null,
    cachePricePerMillionTokens: null,
    cacheWritePricePerMillionTokens: null,
    inputNeuronsPerMillionTokens: null,
    outputNeuronsPerMillionTokens: null,
    cacheNeuronsPerMillionTokens: null,
    cacheWriteNeuronsPerMillionTokens: null,
    unitPrices: [],
  });

  React.useEffect(() => {
    const { typed, extraConfig } = splitModelConfig(model?.config);

    setValues(
      model
        ? {
            providerPublicId: model.providerPublicId,
            taskTypeCode: model.taskTypeCode ?? '',
            taskTypeCodes: modelTaskTypeCodes(model),
            code: model.code,
            name: model.name,
            description: model.description ?? '',
            contextWindow: model.contextWindow ?? undefined,
            ...typed,
            config: extraConfig,
            inputFormatPublicId: model.inputFormatPublicId ?? null,
            fieldOverrides: model.fieldOverrides ?? null,
            usageScope: model.usageScope,
            inputPricePerMillionTokens: model.inputPricePerMillionTokens ?? null,
            outputPricePerMillionTokens: model.outputPricePerMillionTokens ?? null,
            cachePricePerMillionTokens: model.cachePricePerMillionTokens ?? null,
            cacheWritePricePerMillionTokens: model.cacheWritePricePerMillionTokens ?? null,
            inputNeuronsPerMillionTokens: model.inputNeuronsPerMillionTokens ?? null,
            outputNeuronsPerMillionTokens: model.outputNeuronsPerMillionTokens ?? null,
            cacheNeuronsPerMillionTokens: model.cacheNeuronsPerMillionTokens ?? null,
            cacheWriteNeuronsPerMillionTokens: model.cacheWriteNeuronsPerMillionTokens ?? null,
            unitPrices: unitPricesFromModel(model),
            isActive: model.isActive,
          }
        : {
            providerPublicId: providerPublicId ?? providers[0]?.publicId ?? '',
            taskTypeCode: taskTypes[0]?.code ?? '',
            taskTypeCodes: taskTypes[0] ? [taskTypes[0].code] : [],
            code: '',
            name: '',
            description: '',
            contextWindow: undefined,
            defaultTemperature: null,
            defaultMaxOutputTokens: null,
            defaultTopP: null,
            firstChunkTimeoutMs: null,
            streamStallTimeoutMs: null,
            config: '',
            inputFormatPublicId: null,
            fieldOverrides: null,
            usageScope: 'both',
            inputPricePerMillionTokens: null,
            outputPricePerMillionTokens: null,
            cachePricePerMillionTokens: null,
            cacheWritePricePerMillionTokens: null,
            inputNeuronsPerMillionTokens: null,
            outputNeuronsPerMillionTokens: null,
            cacheNeuronsPerMillionTokens: null,
            cacheWriteNeuronsPerMillionTokens: null,
            unitPrices: [],
            isActive: true,
          }
    );
    if (model) {
      if (model.invocationProfiles && model.invocationProfiles.length > 0) {
        setInvocationProfiles(
          model.invocationProfiles.map((p) => {
            let modes: string[] = ['cheap', 'normal', 'expensive'];
            if (p.modes) {
              try {
                const parsed = JSON.parse(p.modes);
                if (Array.isArray(parsed)) modes = parsed;
              } catch {
                // ignore
              }
            }
            return {
              key: p.key,
              method: p.method,
              priority: p.priority ?? 100,
              modes,
              supportsStream: Boolean(p.supportsStream),
              supportsTools: Boolean(p.supportsTools),
              supportsJsonMode: Boolean(p.supportsJsonMode),
              supportsReasoning: Boolean(p.supportsReasoning),
              isDefault: Boolean(p.isDefault),
              isActive: p.isActive !== false,
              providerSlug: p.providerSlug ?? null,
              endpointPath: p.endpointPath ?? null,
              providerModelCode: p.providerModelCode ?? null,
              bodyBuilderKey: p.bodyBuilderKey ?? null,
              parserKey: p.parserKey ?? null,
              schemaOverride: p.schemaOverride ?? null,
              config: p.config ?? null,
            };
          })
        );
      } else {
        setInvocationProfiles(synthesizeLegacyProfiles(model, providers));
      }
    } else {
      const selectedProvider = providers.find((p) => p.publicId === providerPublicId);
      const provCode = selectedProvider?.code?.toLowerCase() || '';
      let initialMethod: LLMInvocationMethod = 'cloudflare_run';
      if (provCode === 'openai') {
        initialMethod = 'openai_chat_completions';
      } else if (provCode === 'anthropic') {
        initialMethod = 'anthropic_messages';
      }
      setInvocationProfiles([
        {
          key: initialMethod,
          method: initialMethod,
          priority: 100,
          modes: ['cheap', 'normal', 'expensive'],
          supportsStream: true,
          supportsTools: initialMethod !== 'cloudflare_run',
          supportsJsonMode:
            initialMethod !== 'cloudflare_run' && initialMethod !== 'anthropic_messages',
          supportsReasoning: initialMethod !== 'cloudflare_run',
          isDefault: true,
          isActive: true,
        },
      ]);
    }
    setActiveTab('basic');
    setJsonErrors({});
    setInvocationError('');
  }, [model, open, providerPublicId, providers, taskTypes]);

  const changeTaskTypes = (codes: string[]) => {
    if (codes.length === 0) return;
    setValues((prev) => ({
      ...prev,
      taskTypeCode: codes[0],
      taskTypeCodes: codes,
    }));
  };
  const validateModelJsonFields = () => {
    const errors: Partial<Record<LLMModelJsonField, string>> = {};
    const configError = validateExtraConfig(values.config);
    if (configError) errors.config = configError;
    setJsonErrors(errors);
    if (Object.keys(errors).length > 0) {
      setActiveTab('basic');
    }
    return Object.keys(errors).length === 0;
  };

  const save = async () => {
    if (!validateModelJsonFields()) return;
    const duplicateUnitPrices = findDuplicateUnitPriceIdentities(values.unitPrices ?? []);
    if (duplicateUnitPrices.length > 0) {
      setInvocationError(`Duplicate unit price rows: ${duplicateUnitPrices.join(', ')}`);
      setActiveTab('basic');
      return;
    }

    const hasBlankResolution = (values.unitPrices ?? []).some(
      (row) => isResolutionPrice(row) && !String(row.condition?.[RESOLUTION_DIMENSION_KEY]).trim()
    );
    if (hasBlankResolution) {
      setInvocationError('Resolution price rows must have a resolution value.');
      setActiveTab('basic');
      return;
    }

    const isModelActive = values.isActive !== false;
    if (isModelActive) {
      const hasActiveProfile = invocationProfiles.some((p) => p.isActive !== false);
      if (!hasActiveProfile) {
        setInvocationError('Active model must have at least one active invocation profile.');
        setActiveTab('invocation');
        return;
      }
    }
    const keys = new Set<string>();
    for (const p of invocationProfiles) {
      const trimmed = p.key.trim();
      if (!trimmed) {
        setInvocationError('Profile key cannot be empty.');
        setActiveTab('invocation');
        return;
      }
      const lower = trimmed.toLowerCase();
      if (keys.has(lower)) {
        setInvocationError(`Duplicate invocation profile key: "${p.key}".`);
        setActiveTab('invocation');
        return;
      }
      keys.add(lower);

      if (p.method === 'gateway_provider_native') {
        if (!p.providerSlug?.trim() || !p.endpointPath?.trim()) {
          setInvocationError(`Profile "${p.key}" requires providerSlug and endpointPath.`);
          setActiveTab('invocation');
          return;
        }
      }
      const methodDef = BUILT_IN_METHOD_DEFINITIONS[p.method as LLMInvocationMethod];
      if (methodDef) {
        if (p.supportsStream && !methodDef.supportsStream) {
          setInvocationError(
            `Profile "${p.key}" cannot enable streaming: method "${p.method}" does not support streaming in registry.`
          );
          setActiveTab('invocation');
          return;
        }
        if (p.supportsTools && !methodDef.supportsTools) {
          setInvocationError(
            `Profile "${p.key}" cannot enable tool calling: method "${p.method}" does not support tools in registry.`
          );
          setActiveTab('invocation');
          return;
        }
        if (p.supportsJsonMode && !methodDef.supportsJsonMode) {
          setInvocationError(
            `Profile "${p.key}" cannot enable JSON mode: method "${p.method}" does not support JSON mode in registry.`
          );
          setActiveTab('invocation');
          return;
        }
        if (p.supportsReasoning && !methodDef.supportsReasoning) {
          setInvocationError(
            `Profile "${p.key}" cannot enable reasoning: method "${p.method}" does not support reasoning in registry.`
          );
          setActiveTab('invocation');
          return;
        }
      }
      const isAnthropic =
        p.method === 'anthropic_messages' ||
        (p.method === 'gateway_provider_native' &&
          (p.bodyBuilderKey === 'anthropic_messages' || p.bodyBuilderKey === 'anthropic'));
      if (isAnthropic) {
        const hasModelTokens =
          values.defaultMaxOutputTokens !== undefined &&
          values.defaultMaxOutputTokens !== null &&
          values.defaultMaxOutputTokens > 0;
        if (!hasModelTokens) {
          setInvocationError(
            `Profile "${p.key}" using Anthropic Messages requires Default Max Output Tokens (Basic tab).`
          );
          setActiveTab('invocation');
          return;
        }
      }
    }

    const config = buildModelConfig(
      {
        defaultTemperature: values.defaultTemperature ?? null,
        defaultMaxOutputTokens: values.defaultMaxOutputTokens ?? null,
        defaultTopP: values.defaultTopP ?? null,
        firstChunkTimeoutMs: values.firstChunkTimeoutMs ?? null,
        streamStallTimeoutMs: values.streamStallTimeoutMs ?? null,
      },
      values.config
    );
    const prices = buildModelPricesPayload(values, unitPricesFromModel(model).length > 0);
    const saveValues = { ...values };
    delete saveValues.unitPrices;
    delete saveValues.inputSchema;
    delete saveValues.outputSchema;
    delete saveValues.defaultTemperature;
    delete saveValues.defaultMaxOutputTokens;
    delete saveValues.defaultTopP;
    delete saveValues.firstChunkTimeoutMs;
    delete saveValues.streamStallTimeoutMs;

    const payload = {
      ...saveValues,
      description: values.description || undefined,
      config: config ?? (model ? null : undefined),
      inputFormatPublicId: values.inputFormatPublicId ?? (model ? null : undefined),
      fieldOverrides: values.fieldOverrides ?? (model ? null : undefined),
      contextWindow: toPositiveInteger(values.contextWindow),
      invocationProfiles: invocationProfiles.map((profile) => ({
        ...profile,
        temperature: null,
        maxOutputTokens: null,
        topP: null,
      })),
      ...(prices === undefined ? {} : { prices }),
    };

    if (model) {
      await updateModel.mutateAsync({ publicId: model.publicId, ...payload });
    } else {
      await createModel.mutateAsync(payload);
    }
    onClose();
  };

  const isInputDirty =
    (values.inputFormatPublicId ?? null) !== (model?.inputFormatPublicId ?? null) ||
    JSON.stringify(values.fieldOverrides ?? null) !== JSON.stringify(model?.fieldOverrides ?? null);

  const handleValueChange = <K extends keyof LLMModelFormValues>(
    field: K,
    value: LLMModelFormValues[K]
  ) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const updateJsonField = (field: LLMModelJsonField, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (jsonErrors[field]) {
      setJsonErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{model ? 'Edit Model' : 'Create Model'}</DialogTitle>
      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(_event, nextTab) => setActiveTab(nextTab)}
          className="mb-4"
        >
          <Tab value="basic" label="Basic" />
          <Tab value="input" label="Input" />
          <Tab
            value="invocation"
            label={
              invocationProfiles.length > 0
                ? `Invocation Methods (${invocationProfiles.length})`
                : 'Invocation Methods'
            }
          />
        </Tabs>

        {invocationError && (
          <Alert severity="error" className="mb-4" onClose={() => setInvocationError('')}>
            {invocationError}
          </Alert>
        )}

        {activeTab === 'basic' && (
          <ModelBasicTab
            values={values}
            modelPublicId={model?.publicId}
            providers={providers}
            taskTypes={taskTypes}
            isLoadingTaskTypes={isLoadingTaskTypes}
            jsonErrors={jsonErrors}
            onValueChange={handleValueChange}
            onTaskTypesChange={changeTaskTypes}
            onJsonFieldChange={updateJsonField}
          />
        )}

        {activeTab === 'input' && (
          <ModelInputTab
            modelPublicId={model?.publicId}
            providerPublicId={values.providerPublicId}
            inputFormatPublicId={values.inputFormatPublicId}
            fieldOverrides={values.fieldOverrides}
            onFormatChange={(publicId) => handleValueChange('inputFormatPublicId', publicId)}
            onOverridesChange={(next) => handleValueChange('fieldOverrides', next)}
            isDirty={isInputDirty}
          />
        )}

        {activeTab === 'invocation' && (
          <ModelInvocationTab
            profiles={invocationProfiles}
            onChangeProfiles={(updated) => {
              setInvocationProfiles(updated);
              setInvocationError('');
            }}
            taskType={taskTypes.find((t) => t.code === values.taskTypeCode)}
            modelCode={values.code}
            isModelActive={values.isActive !== false}
            registryMethods={invocationMethodsData?.methods}
            supportedBodyBuilderKeys={invocationMethodsData?.supportedBodyBuilderKeys}
            supportedParserKeys={invocationMethodsData?.supportedParserKeys}
            supportedNativeBodyBuilderKeys={invocationMethodsData?.supportedNativeBodyBuilderKeys}
            providerCode={providers.find((p) => p.publicId === values.providerPublicId)?.code}
            providerNativeDefaults={invocationMethodsData?.providerNativeDefaults}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={save}
          loading={isSaving}
          disabled={
            !values.providerPublicId || !values.taskTypeCode || !values.code || !values.name
          }
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
